import React, { useState, useEffect } from 'react'
import { DutyFormModalMetaProps, Duty } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'

type Props = {
    meta: DutyFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (dutyData: any) => Promise<void>
    initialDuty?: Duty | null
}

export const DutyFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialDuty } = props

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        negative_list_member: false
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Populate form when editing an event
    useEffect(() => {
        if (initialDuty && isOpen) {
            setFormData({
                name: initialDuty.name,
                description: initialDuty.description || '',
                negative_list_member: initialDuty.negative_list_member || false
            })
        }
        else if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                name: '',
                description: '',
                negative_list_member: false
            })
            setError(null)
        }
    }, [initialDuty, isOpen])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target
        setFormData(prev => ({ ...prev, [name]: checked }))
    }


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            // Format time fields to ensure they're in HH:MM format (remove seconds if present)
            await onSubmit(formData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="2xl"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        variant="outline"
                        color="neutral"
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        type="submit"
                        form="event-form"
                        disabled={loading}
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="event-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4">
                        {/* Duty Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.nameLabel} <span className="text-danger-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.namePlaceholder}
                            />
                        </div>
                        {/* Duty Description */}
                        <div>
                            <label
                                htmlFor="description"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                {"Description Label"}
                            </label>

                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                placeholder={"Enter Description Here"}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                            />
                        </div>

                        {/* Negative List Member checkbox */}
                        {/* <div>
                            <label
                                htmlFor="negative_list_member"
                                className="flex items-center gap-2 cursor-pointer"
                            >
                                <input
                                    id="negative_list_member"
                                    type="checkbox"
                                    name="negative_list_member"
                                    checked={formData.negative_list_member}
                                    onChange={handleCheckboxChange}
                                    className="h-5 w-5 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                                />

                                <span className="text-gray-700 dark:text-gray-300">
                                    {meta.negativeListMemberLabel}
                                </span>
                            </label>
                        </div> */}

                    </div>
                </div>
            </form>
        </Modal >
    )
}
